// useAddReport.js
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import API_BASE_URL from "../config";
import { SPECIAL_DEPENDENT_KEYS } from "../Redux_Services/slices/specialDependentSlice";

// API Endpoint
const API_URL = `${API_BASE_URL}/labTest/SaveResult`;

export default function useAddReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [response, setResponse] = useState(null);
  const specialDependentChecked =
    useSelector((state) => state?.specialDependentSlice?.checkedMap);

  const specialDependentSelection = useMemo(() => {
    const orderedKeys = [
      SPECIAL_DEPENDENT_KEYS.renal,
      SPECIAL_DEPENDENT_KEYS.liver,
       SPECIAL_DEPENDENT_KEYS.thyroid,
    ];

    const selected = orderedKeys.filter(
      (key) => specialDependentChecked?.[key]
    );

    return selected.length > 0 ? selected.join(", ") : "none";
  }, [specialDependentChecked]);

  /**
   * Submit the lab result WITHOUT encryption.
   * @param {object} params - { PatientID, ReferredBy, MachineID, Report, token }
   */
  const addReport = async ({ PatientID, ReferredBy, MachineID, Report, token }) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    setResponse(null);

    try {
      // 1️⃣ Build the plain payload
      const body = {
        PatientID,
        ReferredBy,
        MachineID,
        Report,
        specialDependentTests: specialDependentSelection,
      };

      // 2️⃣ Send plain JSON payload to server
      const res = await axios.post(API_URL, body, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      // 3️⃣ Response is already plain, no decryption needed
      const plainResponse = res?.data ?? null;

      if (plainResponse?.error === true) {
        const apiMessage =
          plainResponse?.message ||
          plainResponse?.data?.message ||
          "Unable to save result.";
        setError(apiMessage);
        setSuccess(false);
        setResponse(plainResponse);
        return plainResponse;
      }

      // 4️⃣ Store in state
      setResponse(plainResponse);
      setSuccess(true);

      return plainResponse;
    } catch (err) {
      console.error("Add report error:", err);

      const apiPayload = err?.response?.data;
      const apiMessage =
        apiPayload?.message ||
        apiPayload?.details ||
        err?.message ||
        "An error occurred";

      setError(
        apiMessage
      );
      setResponse(apiPayload || null);
      setSuccess(false);
      return {
        error: true,
        message: apiMessage,
        details: apiPayload?.details || null,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    success,
    response,
    addReport,
  };
}
